import ApiContainer from '@/orm/ApiContainer'

/**
 * Holds the feature flags for the current request, delivered CASCADE-RESOLVED
 * from GET /v2/feature (VF-4173). The backend resolves the three-tier cascade
 * (global / active customer / user) per flag and returns a boolean, so the SPA
 * simply reads the effective on/off value — it does NOT re-resolve tiers here.
 *
 * The blob is re-fetched on boot and whenever settings change (see
 * UserModule socket handlers), so switching customer context or flipping a flag
 * surfaces on the next fetch.
 *
 * Auto-registers as `featureModule` via store/modules/index.js.
 */
export default {
  state: {
    /**
     * Resolved feature flags keyed by setting key, e.g.
     * { 'feature.configurableTableColumns.enabled': true }. Empty until fetched.
     */
    flags: {}
  },

  mutations: {
    /**
     * Replace the resolved-flag map from a list of { key, value } rows, where
     * value is the backend-resolved boolean.
     *
     * @param {*} state
     * @param {Array} rows
     */
    setFlags (state, rows) {
      const flags = {}
      ;(rows || []).forEach(row => {
        flags[row.key] = Boolean(row.value)
      })
      state.flags = flags
    }
  },

  actions: {
    /**
     * Fetch the resolved feature flags from GET /v2/feature and commit them.
     * Called on app boot and on the *-settings-updated / application-release
     * socket events.
     *
     * @param {*} context
     * @return {Promise}
     */
    fetchFeatures (context) {
      return ApiContainer.getMapper('Feature')
        .findAll()
        .then((response) => {
          // findAll resolves to a PagedCollection (see BaseMapper), not a raw
          // array — pull the records out before committing.
          const rows = typeof response.getItems === 'function' ? response.getItems() : response
          context.commit('setFlags', rows)
        })
    }
  },

  getters: {
    /**
     * The resolved value of a feature flag (false if unknown/not delivered).
     * This is the effective, cascade-resolved state from the backend.
     *
     * @param {*} state
     * @return {Function} (key) => Boolean
     */
    getFeatureFlag: state => key => Boolean(state.flags[key]),

    /**
     * The single gate for Configurable Table Columns (VF-4173/4174). Thin
     * wrapper over getFeatureFlag. The ColumnConfigurator and adopting tables
     * use ONLY this getter.
     *
     * @param {*} state
     * @param {*} getters
     * @return {Boolean}
     */
    configurableTableColumnsEnabled: (state, getters) =>
      getters.getFeatureFlag('feature.configurableTableColumns.enabled'),

    /**
     * When on, root users are kept on the Vue 3 UI: AppLayout redirects them to
     * /v3 and the TopBar "New UI" toggle is hidden. Off by default; setting the
     * global row to '0' is the kill switch. Mirrored in vue3/src/stores/feature.js.
     *
     * @param {*} state
     * @param {*} getters
     * @return {Boolean}
     */
    forceNewUIForRoot: (state, getters) =>
      getters.getFeatureFlag('feature.forceNewUIForRoot.enabled'),

    /**
     * When on, every user is kept on the Vue 3 UI: AppLayout redirects them to
     * /v3. Off when the flag row is absent or false.
     *
     * @param {*} state
     * @param {*} getters
     * @return {Boolean}
     */
    forceNewUI: (state, getters) =>
      getters.getFeatureFlag('feature.forceNewUI.enabled')
  }
}
